import React, { useContext, useState, useRef, useEffect } from "react";
import Pagereview from "../../components/pageReview/Pagereview";
import { FormContext } from "../CMSRenderer/CMSRenderer";
import { CountriesList } from "../../components/pageReview/countrieslist";
import { PiPlusCircle } from "react-icons/pi";

const DatePicker = ({ value, onChange, placeholder, name }) => {
  const [date, setDate] = useState(value || "");

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    onChange({ target: { name, value: newDate } });
  };

  return (
    <input
      type="date"
      value={date}
      onChange={handleDateChange}
      placeholder={placeholder}
      className="w-full p-4 border border-[#e5e7eb] rounded-md"
      name={name}
    />
  );
};

const FileUpload = ({ onFilesChange, files = [], currentLang, config }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter(
      (file) =>
        file.type.includes("pdf") ||
        file.type.includes("image") ||
        file.name.match(/\.(svg|png|jpe?g|pdf)$/i)
    );

    const updatedFiles = [...files, ...validFiles];
    onFilesChange(updatedFiles);
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <div
        className={`border-2 border-dashed rounded-lg p-8 cursor-pointer transition-colors flex items-start gap-4 ${
          isDragging
            ? "border-primary bg-blue-50"
            : "border-outline hover:border-primary"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}>
        <PiPlusCircle className="text-primary mt-1 flex-shrink-0" size={36} />

        <div>
          <p className="text-onSurfaceVariant mb-2 font-medium">
            {currentLang === "en" 
              ? config?.drag_drop_text 
              : config?.drag_drop_text_ar}
          </p>
          <p className="text-sm text-onSurfaceVariant">
            {currentLang === "en" 
              ? config?.supported_formats 
              : config?.supported_formats_ar}
          </p>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.svg"
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="mt-4">
          <h6 className="mb-2">
            {currentLang === "en" 
              ? config?.uploaded_files 
              : config?.uploaded_files_ar}
          </h6>
          <div className="space-y-2">
            {files?.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border rounded-lg bg-surface">
                <div className="flex items-center space-x-3">
                  <i className="material-icons text-primary">description</i>
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-outline">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-error hover:text-red-700">
                  <i className="material-icons">delete</i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const WorkPlacement = ({ config }) => {
  const { currentLang } = useContext(FormContext);
  const [activeAccordion, setActiveAccordion] = useState(0);
  const [completedAccordions, setCompletedAccordions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    passportNumber: "",
    emiratesId: "",
    universityName: "",
    studentId: "",
    major: "",
    coordinatorName: "",
    coordinatorEmail: "",
    firstChoiceDepartment: "",
    secondChoiceDepartment: "",
    startDate: "",
    endDate: "",
    dailyHours: "",
    trainingObjective: "",
  });

  const [selectedCountry, setSelectedCountry] = useState(CountriesList[0]);
  const [coordinatorCountry, setCoordinatorCountry] = useState(CountriesList[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [coordinatorDropdownOpen, setCoordinatorDropdownOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [coordinatorPhone, setCoordinatorPhone] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
  };

  const handleSelectCoordinatorCountry = (country) => {
    setCoordinatorCountry(country);
    setCoordinatorDropdownOpen(false);
  };

  const handleNext = (nextIndex) => {
    // Mark the current accordion as completed
    setCompletedAccordions(prev => {
      const updated = [...prev];
      // Mark all previous accordions up to the current one as completed
      for (let i = 0; i < nextIndex; i++) {
        if (!updated.includes(i)) {
          updated.push(i);
        }
      }
      return updated;
    });
    
    setActiveAccordion(nextIndex);
  };

  const handleFilesChange = (files) => {
    setUploadedFiles(files);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.email.trim()) newErrors.email = currentLang === "en" ? "Required" : "مطلوب";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!phoneNumber.trim()) newErrors.phoneNumber = currentLang === "en" ? "Required" : "مطلوب";
    else if (!/^\d+$/.test(phoneNumber)) newErrors.phoneNumber = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!formData.passportNumber.trim()) newErrors.passportNumber = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.emiratesId.trim()) newErrors.emiratesId = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.universityName.trim()) newErrors.universityName = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.studentId.trim()) newErrors.studentId = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.major.trim()) newErrors.major = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.coordinatorName.trim()) newErrors.coordinatorName = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!coordinatorPhone.trim()) newErrors.coordinatorPhone = currentLang === "en" ? "Required" : "مطلوب";
    else if (!/^\d+$/.test(coordinatorPhone)) newErrors.coordinatorPhone = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!formData.coordinatorEmail.trim()) newErrors.coordinatorEmail = currentLang === "en" ? "Required" : "مطلوب";
    else if (!/\S+@\S+\.\S+/.test(formData.coordinatorEmail)) newErrors.coordinatorEmail = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!formData.firstChoiceDepartment.trim()) newErrors.firstChoiceDepartment = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.secondChoiceDepartment.trim()) newErrors.secondChoiceDepartment = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.startDate) newErrors.startDate = currentLang === "en" ? "Required" : "مطلوب";
    if (!formData.endDate) newErrors.endDate = currentLang === "en" ? "Required" : "مطلوب";
    
    if (!formData.dailyHours) newErrors.dailyHours = currentLang === "en" ? "Required" : "مطلوب";
    else if (formData.dailyHours < 1 || formData.dailyHours > 24) newErrors.dailyHours = currentLang === "en" ? "Required" : "مطلوب";
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) newErrors.endDate = currentLang === "en" ? "Required" : "مطلوب";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSubmitMessage(currentLang === "en" 
        ? config?.validation_error 
        : config?.validation_error_ar);
      return;
    }

    setSubmitMessage("");

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      formDataToSend.append("fullPhoneNumber", `${selectedCountry.code}${phoneNumber}`);
      formDataToSend.append("coordinatorFullPhone", `${coordinatorCountry.code}${coordinatorPhone}`);
      
      uploadedFiles.forEach(file => {
        formDataToSend.append("documents", file);
      });

      const response = await fetch("/api/content/createWorkPlacement", {
        method: "POST",
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.code === 200) {
        setSubmitMessage(currentLang === "en" 
          ? config?.success_message 
          : config?.success_message_ar);
        
        setFormData({
          name: "",
          email: "",
          passportNumber: "",
          emiratesId: "",
          universityName: "",
          studentId: "",
          major: "",
          coordinatorName: "",
          coordinatorEmail: "",
          firstChoiceDepartment: "",
          secondChoiceDepartment: "",
          startDate: "",
          endDate: "",
          dailyHours: "",
          trainingObjective: "",
        });
        setPhoneNumber("");
        setCoordinatorPhone("");
        setUploadedFiles([]);
        setActiveAccordion(0);
        setCompletedAccordions([]);
      } else {
        setSubmitMessage(result.error || (currentLang === "en" 
          ? config?.submission_error 
          : config?.submission_error_ar));
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitMessage(currentLang === "en" 
        ? config?.network_error 
        : config?.network_error_ar);
    }
  };

  const breadcrumbData = [
    {
      text: currentLang === "en" ? config?.home : config?.home_ar,
      icon: "home",
      url: "/",
    },
    {
      text: currentLang === "en" ? config?.page_title : config?.page_title_ar,
      icon: "menu_book",
      url: "/workPlacement",
    },
  ]; 

  // Helper function to determine accordion class
  const getAccordionClass = (accordionIndex) => {
    const baseClass = "border rounded-xl mb-4";
    if (completedAccordions.includes(accordionIndex)) {
      return `${baseClass} completed`;
    }
    return baseClass;
  };

  return (
    <>
      <dda-breadcrumb
        design="text"
        separator="chevron_right"
        custom_class=""
        component_mode=""
        data-breadcrumbs={JSON.stringify(breadcrumbData)}></dda-breadcrumb>

      <div className="mb-4">
        <img
          className="w-full h-[200px] md:h-[300px] object-cover rounded-xl mb-4"
          src={config?.bannerimage || `/attachments/medialib/e59ab741-433e-4f3d-bd79-cfca79e6b42c-1759824556862-578781064-workPplacement.jpg`}
          alt="Hero img"
        />
      </div>

      <h2 className="mb-4 Medium text-primary">
        {currentLang === "en" ? config?.page_title : config?.page_title_ar}
      </h2>

      <p className="text-onSurfaceVariant mb-6">
        {currentLang === "en" ? config?.description : config?.description_ar}
      </p>

      <div className="lg:flex gap-6">
        {/* Main Content - 4/5 width */}
        <div className="lg:w-4/5">
          

          {/* Submit Message */}
          {submitMessage && (
            <div className={`p-4 mb-4 rounded-lg ${
              submitMessage.includes("successfully") || submitMessage.includes("تم تقديم الطلب بنجاح")
                ? "bg-green-100 text-green-800 border border-green-300" 
                : "bg-red-100 text-red-800 border border-red-300"
            }`}>
              {submitMessage}
            </div>
          )}

          {/* Multi-step Form */}
          <form onSubmit={handleSubmit}>
            {/* Accordion 1 - Applicant Details */}
            <div className={getAccordionClass(0)}>
              <button
                type="button"
                className="w-full flex justify-between p-4 text-left bg-surface rounded-t-xl"
                onClick={() => setActiveAccordion(activeAccordion === 0 ? -1 : 0)}>
                <h6 className="Medium text-primary">
                  {currentLang === "en" ? config?.accordion1_title : config?.accordion1_title_ar}
                </h6>
                <span class="flex items-center justify-center w-[36px] h-[36px] rounded-full border-2 border-[var(--dda-primary-40)] bg-[var(--dda-color-teal-variant)]">1</span>
              </button>

              {activeAccordion === 0 && (
                <div className="p-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.name_placeholder : config?.name_placeholder_ar}
                      value={formData.name}
                      type="text"
                      size="default"
                      error_message={errors.name || ""}
                      custom_class={`w-full ${errors.name ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "name", value: e.target.value } })}
                      input_name="name"
                    ></dda-input>
                    {errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
                  </div>

                  <div>
                    <div className="dda-input-field-group dda-phone-field">
                      <div className="dda-input-dropdown-btn" style={{ minWidth: "unset" }}>
                        <button
                          type="button"
                          className="dda-dropdown-select"
                          onClick={() => setDropdownOpen((prev) => !prev)}>
                          <img src={selectedCountry.flag} alt="" width="20" />
                          <span>{selectedCountry.code}</span>
                          <i className="material-icons">keyboard_arrow_down</i>
                        </button>

                        {dropdownOpen && (
                          <div className="dda-input-dropdown-list">
                            {CountriesList?.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                className="country-select dda-input-dropdown-item"
                                onClick={() => handleSelectCountry(country)}>
                                <img src={country.flag} alt="" width="20" />
                                {country.code}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <input
                        type="tel"
                        name="phoneNumber"
                        placeholder={currentLang === "en" ? config?.phone_placeholder : config?.phone_placeholder_ar}
                        className={`dda-field-group-input ${errors.phoneNumber ? "border-red-500" : ""}`}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </div>
                    {errors.phoneNumber && <div className="text-red-500 text-sm mt-1">{errors.phoneNumber}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.email_placeholder : config?.email_placeholder_ar}
                      value={formData.email}
                      type="email"
                      size="default"
                      custom_class={`w-full ${errors.email ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "email", value: e.target.value } })}
                      input_name="email"
                    ></dda-input>
                    {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.passport_placeholder : config?.passport_placeholder_ar}
                      value={formData.passportNumber}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.passportNumber ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "passportNumber", value: e.target.value } })}
                      input_name="passportNumber"
                    ></dda-input>
                    {errors.passportNumber && <div className="text-red-500 text-sm mt-1">{errors.passportNumber}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.emirates_id_placeholder : config?.emirates_id_placeholder_ar}
                      value={formData.emiratesId}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.emiratesId ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "emiratesId", value: e.target.value } })}
                      input_name="emiratesId"
                    ></dda-input>
                    {errors.emiratesId && <div className="text-red-500 text-sm mt-1">{errors.emiratesId}</div>}
                  </div>

                  <div className="flex justify-end md:col-span-2">
                    <dda-button
                      button_color="default-primary"
                      button_text={currentLang === "en" ? config?.next_button : config?.next_button_ar}
                      end_icon=""
                      custom_class="px-8"
                      onclick={() => handleNext(1)}
                    ></dda-button>
                  </div>
                </div>
              )}
            </div>

            {/* Accordion 2 - Educational Institution Details */}
            <div className={getAccordionClass(1)}>
              <button
                type="button"
                className="w-full flex justify-between p-4 text-left bg-surface rounded-t-xl"
                onClick={() => setActiveAccordion(activeAccordion === 1 ? -1 : 1)}>
                <h6 className="Medium text-primary">
                  {currentLang === "en" ? config?.accordion2_title : config?.accordion2_title_ar}
                </h6>
                <span class="flex items-center justify-center w-[36px] h-[36px] rounded-full border-2 border-[var(--dda-primary-40)] bg-[var(--dda-color-teal-variant)]">2</span>
              </button>

              {activeAccordion === 1 && (
                <div className="p-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.university_placeholder : config?.university_placeholder_ar}
                      value={formData.universityName}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.universityName ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "universityName", value: e.target.value } })}
                      input_name="universityName"
                    ></dda-input>
                    {errors.universityName && <div className="text-red-500 text-sm mt-1">{errors.universityName}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.student_id_placeholder : config?.student_id_placeholder_ar}
                      value={formData.studentId}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.studentId ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "studentId", value: e.target.value } })}
                      input_name="studentId"
                    ></dda-input>
                    {errors.studentId && <div className="text-red-500 text-sm mt-1">{errors.studentId}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.major_placeholder : config?.major_placeholder_ar}
                      value={formData.major}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.major ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "major", value: e.target.value } })}
                      input_name="major"
                    ></dda-input>
                    {errors.major && <div className="text-red-500 text-sm mt-1">{errors.major}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.coordinator_name_placeholder : config?.coordinator_name_placeholder_ar}
                      value={formData.coordinatorName}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.coordinatorName ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "coordinatorName", value: e.target.value } })}
                      input_name="coordinatorName"
                    ></dda-input>
                    {errors.coordinatorName && <div className="text-red-500 text-sm mt-1">{errors.coordinatorName}</div>}
                  </div>

                  {/* Coordinator Phone Field */}
                  <div>
                    <div className="dda-input-field-group dda-phone-field">
                      <div className="dda-input-dropdown-btn" style={{ minWidth: "unset" }}>
                        <button
                          type="button"
                          className="dda-dropdown-select"
                          onClick={() => setCoordinatorDropdownOpen((prev) => !prev)}>
                          <img src={coordinatorCountry.flag} alt="" width="20" />
                          <span>{coordinatorCountry.code}</span>
                          <i className="material-icons">keyboard_arrow_down</i>
                        </button>

                        {coordinatorDropdownOpen && (
                          <div className="dda-input-dropdown-list">
                            {CountriesList?.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                className="country-select dda-input-dropdown-item"
                                onClick={() => handleSelectCoordinatorCountry(country)}>
                                <img src={country.flag} alt="" width="20" />
                                {country.code}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <input
                        type="tel"
                        name="coordinatorPhone"
                        placeholder={currentLang === "en" ? config?.coordinator_phone_placeholder : config?.coordinator_phone_placeholder_ar}
                        className={`dda-field-group-input ${errors.coordinatorPhone ? "border-red-500" : ""}`}
                        value={coordinatorPhone}
                        onChange={(e) => setCoordinatorPhone(e.target.value)}
                      />
                    </div>
                    {errors.coordinatorPhone && <div className="text-red-500 text-sm mt-1">{errors.coordinatorPhone}</div>}
                  </div>

                  {/* Coordinator Email Field */}
                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.coordinator_email_placeholder : config?.coordinator_email_placeholder_ar}
                      value={formData.coordinatorEmail}
                      type="email"
                      size="default"
                      custom_class={`w-full ${errors.coordinatorEmail ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "coordinatorEmail", value: e.target.value } })}
                      input_name="coordinatorEmail"
                    ></dda-input>
                    {errors.coordinatorEmail && <div className="text-red-500 text-sm mt-1">{errors.coordinatorEmail}</div>}
                  </div>

                  <div className="flex justify-end md:col-span-2">
                    <dda-button
                      button_color="default-primary"
                      button_text={currentLang === "en" ? config?.next_button : config?.next_button_ar}
                      end_icon=""
                      custom_class="px-8"
                      onclick={() => handleNext(2)}
                    ></dda-button>
                  </div>
                </div>
              )}
            </div>

            {/* Accordion 3 - Work Placement Details */}
            <div className={getAccordionClass(2)}>
              <button
                type="button"
                className="w-full flex justify-between p-4 text-left bg-surface rounded-t-xl"
                onClick={() => setActiveAccordion(activeAccordion === 2 ? -1 : 2)}>
                <h6 className="Medium text-primary">
                  {currentLang === "en" ? config?.accordion3_title : config?.accordion3_title_ar}
                </h6>
                <span class="flex items-center justify-center w-[36px] h-[36px] rounded-full border-2 border-[var(--dda-primary-40)] bg-[var(--dda-color-teal-variant)]">3</span>
              </button>

              {activeAccordion === 2 && (
                <div className="p-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.first_choice_placeholder : config?.first_choice_placeholder_ar}
                      value={formData.firstChoiceDepartment}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.firstChoiceDepartment ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "firstChoiceDepartment", value: e.target.value } })}
                      input_name="firstChoiceDepartment"
                    ></dda-input>
                    {errors.firstChoiceDepartment && <div className="text-red-500 text-sm mt-1">{errors.firstChoiceDepartment}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.second_choice_placeholder : config?.second_choice_placeholder_ar}
                      value={formData.secondChoiceDepartment}
                      type="text"
                      size="default"
                      custom_class={`w-full ${errors.secondChoiceDepartment ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "secondChoiceDepartment", value: e.target.value } })}
                      input_name="secondChoiceDepartment"
                    ></dda-input>
                    {errors.secondChoiceDepartment && <div className="text-red-500 text-sm mt-1">{errors.secondChoiceDepartment}</div>}
                  </div>

                  <div>
                    <label className="block text-sm mb-2 text-onSurfaceVariant">
                      {currentLang === "en" ? config?.start_date_label : config?.start_date_label_ar}
                    </label>
                    <DatePicker
                      value={formData.startDate}
                      onChange={handleChange}
                      placeholder={currentLang === "en" ? config?.start_date_placeholder : config?.start_date_placeholder_ar}
                      name="startDate"
                    />
                    {errors.startDate && <div className="text-red-500 text-sm mt-1">{errors.startDate}</div>}
                  </div>

                  <div>
                    <label className="block text-sm mb-2 text-onSurfaceVariant">
                      {currentLang === "en" ? config?.end_date_label : config?.end_date_label_ar}
                    </label>
                    <DatePicker
                      value={formData.endDate}
                      onChange={handleChange}
                      placeholder={currentLang === "en" ? config?.end_date_placeholder : config?.end_date_placeholder_ar}
                      name="endDate"
                    />
                    {errors.endDate && <div className="text-red-500 text-sm mt-1">{errors.endDate}</div>}
                  </div>

                  <div>
                    <dda-input
                      placeholder={currentLang === "en" ? config?.daily_hours_placeholder : config?.daily_hours_placeholder_ar}
                      value={formData.dailyHours}
                      type="number"
                      size="default"
                      custom_class={`w-full ${errors.dailyHours ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "dailyHours", value: e.target.value } })}
                      input_name="dailyHours"
                    ></dda-input>
                    {errors.dailyHours && <div className="text-red-500 text-sm mt-1">{errors.dailyHours}</div>}
                  </div>

                  <div className="md:col-span-2">
                    <dda-textarea
                      placeholder={currentLang === "en" ? config?.training_objective_placeholder : config?.training_objective_placeholder_ar}
                      value={formData.trainingObjective}
                      max_characters="500"
                      custom_class={`w-full ${errors.trainingObjective ? "error" : ""}`}
                      component_mode=""
                      onchange={(e) => handleChange({ target: { name: "trainingObjective", value: e.target.value } })}
                      textarea_name="trainingObjective"
                    ></dda-textarea>
                    {errors.trainingObjective && <div className="text-red-500 text-sm mt-1">{errors.trainingObjective}</div>}
                  </div>

                  <div className="flex justify-end md:col-span-2">
                    <dda-button
                      button_color="default-primary"
                      button_text={currentLang === "en" ? config?.next_button : config?.next_button_ar}
                      end_icon=""
                      custom_class="px-8"
                      onclick={() => handleNext(3)}
                    ></dda-button>
                  </div>
                </div>
              )}
            </div>

            {/* Accordion 4 - File Upload */}
            <div className={getAccordionClass(3)}>
              <button
                type="button"
                className="w-full flex justify-between p-4 text-left bg-surface rounded-t-xl"
                onClick={() => setActiveAccordion(activeAccordion === 3 ? -1 : 3)}>
                <h6 className="Medium text-primary">
                  {currentLang === "en" ? config?.accordion4_title : config?.accordion4_title_ar}
                </h6>
                <span class="flex items-center justify-center w-[36px] h-[36px] rounded-full border-2 border-[var(--dda-primary-40)] bg-[var(--dda-color-teal-variant)]">4</span>
              </button>

              {activeAccordion === 3 && (
                <div className="p-6">
                  <FileUpload
                    onFilesChange={handleFilesChange}
                    files={uploadedFiles}
                    currentLang={currentLang}
                    config={config}
                  />

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className={`bg-primary text-white px-8 py-2 rounded-lg flex items-center gap-2`}>
                      <span>
                        {currentLang === "en" ? config?.submitting_text : config?.submitting_text_ar}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Sidebar - 1/5 width */}
        <div className="lg:w-1/5 lg:!mt-0 mt-6">
          {/* Eligibility Criteria Accordion */}
          <dda-accordion
            design="bg-border"
            accordion_icon_class="material-icons material-symbols-outlined"
            accordion_icon_text="description"
            accordion_header_end_icon=""
            accordion_id="eligibility-criteria"
            header_text={currentLang === "en" ? config?.eligibility_title : config?.eligibility_title_ar}
            custom_class="mb-4"
            component_mode=""
            default_open="true">
            <div className="text-onSurfaceVariant text-sm">
              {currentLang === "en" ? (
                <ul className="list-disc !ps-4 space-y-2">
                  {config?.eligibility_criteria?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <ul className="list-disc !ps-4 space-y-2" dir="rtl">
                  {config?.eligibility_criteria_ar?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </dda-accordion>

          {/* Required Documents Accordion */}
          <dda-accordion
            design="bg-border"
            accordion_icon_class="material-icons material-symbols-outlined"
            accordion_icon_text="description"
            accordion_header_end_icon=""
            accordion_id="required-documents"
            header_text={currentLang === "en" ? config?.documents_title : config?.documents_title_ar}
            custom_class="mb-4"
            component_mode=""
            default_open="true">
            <div className="text-onSurfaceVariant text-sm">
              {currentLang === "en" ? (
                <ul className="list-disc !ps-4 space-y-2">
                  {config?.required_documents?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <ul className="list-disc !ps-4 space-y-2" dir="rtl">
                  {config?.required_documents_ar?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </dda-accordion>

          {/* Terms and Conditions Accordion */}
          <dda-accordion
            design="bg-border"
            accordion_icon_class="material-icons material-symbols-outlined"
            accordion_icon_text="description"
            accordion_header_end_icon=""
            accordion_id="terms-conditions"
            header_text={currentLang === "en" ? config?.terms_title : config?.terms_title_ar}
            custom_class="mb-4"
            component_mode=""
            default_open="true">
            <div className="text-onSurfaceVariant text-sm">
              {currentLang === "en" ? (
                <ul className="list-disc !ps-4 space-y-2">
                  {config?.terms_conditions?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <ul className="list-disc !ps-4 space-y-2" dir="rtl">
                  {config?.terms_conditions_ar?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </dda-accordion>
        </div>
      </div>

      
    </>
  );
};

export default WorkPlacement;